<template>
  <div>
    <template v-if="!getSelfContactListPending">
      <app-alert-in-page variant="info">
        {{ $t("archives.ArchivesShareModule.alertText") }}
      </app-alert-in-page>

      <v-checkbox class="my-2"
                  :model-value="allMembersChecked"
                  :indeterminate="someMembersChecked"
                  :label="$t('archives.ArchivesShareModule.inputSelectAllMembersLabel')"
                  @update:model-value="toggleSelectAllMembers"
      />

      <div class="members-container pb-4 ml-2">
        <template v-for="(userRow, i) in knownUsersMatrix" :key="i">
          <v-row class="row-border pb-2 mt-2" no-gutters align="center">
            <template v-for="user in userRow" :key="user.id">
              <v-col :cols="3">
                <v-checkbox v-model="value.fileRights"
                            :disabled="user.id === profile.id || user.isArchivingAdmin"
                            :value="user.id"
                            :label="user.firstName + ' ' + user.lastName"
                            hide-details
                            class="mt-0 px-2"
                />
              </v-col>
            </template>
          </v-row>
        </template>
      </div>
      <v-checkbox v-model="value.notify" :label="$t('archives.ArchivesShareModule.inputNotifyLabel')"/>
      <v-textarea v-show="value.notify"
                  id="notify-message-input"
                  class="notify-message-field"
                  v-model="value.notifyMessage"
                  variant="outlined"
                  color="border-info-bold"
                  :label="$t('archives.ArchivesShareModule.inputNotifyMessageLabel')"
      />
    </template>
    <div v-else class="text-center">
      <v-progress-circular style="width: 64px; height: 64px"
                           indeterminate
                           size="64"
      />
    </div>
  </div>
</template>

<script>
import { chunk } from 'lodash-es'
import { createNamespacedHelpers } from 'vuex'

import { GET_SELF_CONTACT_LIST } from '@/store/modules/user/action_types'

const { mapActions, mapState } = createNamespacedHelpers('user')
const NUMBER_OF_USER_COLUMNS = 4
/**
 * Check if a provided user is an archivingAdmin not already in the userArray
 * @param {{ id: number, isArchivingAdmin }} user User object
 * @param {array} userArray user ids array to check from
 * @returns {boolean}
 */
function uniqueArchivingAdminUser (user, userArray) {
  return !userArray.includes(user.id) && user.isArchivingAdmin
}

export default {
  name: 'ArchivesShareModule',
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      initialFileRights: [],
      knownUsers: [],
    }
  },
  computed: {
    allMembersChecked () {
      return this.value.fileRights && this.value.fileRights.length === this.knownUsers.length
    },
    someMembersChecked () {
      return this.value.fileRights && this.value.fileRights.length !== 1 && !this.allMembersChecked
    },
    ...mapState(['selfContactListFromSubscription', 'getSelfContactListPending', 'profile']),
    knownUsersMatrix () {
      return chunk(this.knownUsers, NUMBER_OF_USER_COLUMNS)
    },
  },
  async created () {
    await this.GET_SELF_CONTACT_LIST({ fromUserSubscriptionOnly: true })
    this.knownUsers = this.selfContactListFromSubscription
    // fileRights should always be an array so we can do array methods on it
    if (!Array.isArray(this.value.fileRights)) {
      this.value.fileRights = []
    }
    // We always add the current user and the archiving admins by default
    this.initialFileRights = [this.profile.id]
    this.initialFileRights = this.initialFileRights.concat(
      this.selfContactListFromSubscription
        .filter((user) => uniqueArchivingAdminUser(user, [...this.value.fileRights, ...this.initialFileRights]))
        .map((user) => user.id),
    )
    this.value.fileRights = [...this.initialFileRights, ...this.value.fileRights]
  },
  methods: {
    ...mapActions([GET_SELF_CONTACT_LIST]),
    toggleSelectAllMembers () {
      // If we already checked everything, we should deselect them and go back to the initialFileRights
      // otherwise, select them all
      if (this.allMembersChecked) {
        this.value.fileRights = [...this.initialFileRights]
      } else {
        this.value.fileRights = this.knownUsers.map((user) => {
          return user.id
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.members-container {
  max-height: 200px;
  overflow-y: auto;
}

.row-border {
  border-bottom: get-border();
}

.notify-message-field {
  --outline-color: rgb(var(--v-theme-border-secondary));
  --outline-width: #{get-width('border-width-1')};
  --text-color: rgb(var(--v-theme-text-secondary));

  &:focus-within {
    --outline-color: rgb(var(--v-theme-border-info-bold));
    --outline-width: #{get-width('border-width-2')};
  }

  &.v-input--error {
    --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
  }

  :deep(.v-field-label:not(.v-field-label--floating)) {
    color: rgb(var(--v-theme-text-placeholder));
  }

  :deep(.v-field__input)::placeholder {
    color: rgb(var(--v-theme-text-placeholder));
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field--active.v-field--variant-outlined .v-field__outline__notch::before) {
    opacity: 0;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }

  &.v-input--error :deep(.v-messages) {
    color: rgb(var(--v-theme-text-danger)) !important;
  }
}
</style>
